import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Trophy, IdentificationBadge, MapPin, Briefcase, Users } from "@phosphor-icons/react";

/**
 * v3.2.28 · Admin analytics — PEs ranked by client demand (times
 * favorited). Auto-boost in the public directory is already active
 * (see `list_public_pes` in pe_profile.py); this page gives admin the
 * numeric backing so they can confirm the ordering matches actual
 * hiring signals.
 */
export default function AdminPEAnalyticsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(true);

  const assetUrl = (ref) => (ref ? `${getBackendUrl()}/api/pe/assets/${ref}` : "");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get("/admin/pe-favorites-analytics");
        setRows(data.rows || []);
        setTotal(data.total || 0);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load analytics");
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  const topThree = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-analytics" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="admin-analytics-heading">
          PE Demand Analytics
        </h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Ranked by times favorited across every client. The directory
          view auto-boosts PEs with more favorites to the top of default
          (years-based) results.
        </p>

        {busy ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="admin-analytics-loading">
            Loading analytics…
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="admin-analytics-empty">
            No PEs on file yet.
          </div>
        ) : (
          <>
            {/* Podium — top 3 favorites */}
            {topThree.some((r) => r.favorited_count > 0) && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-analytics-podium">
                {topThree.map((r, i) => (
                  <div key={r.id}
                    onClick={() => navigate(`/directory/pes/${r.id}`)}
                    className={`relative border p-4 cursor-pointer transition ${
                      i === 0 ? "border-[#FFD700]/60 bg-[#FFD700]/[0.03] hover:border-[#FFD700]"
                      : i === 1 ? "border-[#00E5FF]/40 bg-[#00E5FF]/[0.02] hover:border-[#00E5FF]"
                      : "border-white/15 bg-white/[0.02] hover:border-white/30"
                    }`}
                    data-testid={`admin-analytics-podium-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy size={14} weight="fill"
                        className={i === 0 ? "text-[#FFD700]" : i === 1 ? "text-[#00E5FF]" : "text-gray-500"} />
                      <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
                        Rank #{i + 1}
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 border border-white/10 bg-black/40 overflow-hidden flex-none">
                        {r.picture_ref
                          ? <img src={assetUrl(r.picture_ref)} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-gray-600"><IdentificationBadge size={26} weight="duotone" /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-bold truncate">{r.name || "—"}</div>
                        <div className="text-[10px] font-mono text-gray-500 truncate">{r.email}</div>
                        <div className="mt-2 flex items-center gap-3 text-xs font-mono">
                          <span className="inline-flex items-center gap-1 text-[#FF8A85]"
                            data-testid={`admin-analytics-podium-count-${i}`}>
                            <Heart size={12} weight="fill" /> {r.favorited_count}
                          </span>
                          <span className="inline-flex items-center gap-1 text-gray-400">
                            <Users size={12} /> {r.unique_client_count} clients
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Full table */}
            <div className="mt-6 border border-white/10" data-testid="admin-analytics-table">
              <div className="grid grid-cols-[2fr_110px_110px_1fr_100px] gap-2 px-4 py-2 border-b border-white/10 bg-black/40 font-mono text-[10px] uppercase tracking-wider text-gray-500">
                <div>PE</div>
                <div>Favorites</div>
                <div>Clients</div>
                <div>Location · Years</div>
                <div>Status</div>
              </div>
              {[...topThree, ...rest].map((r) => (
                <div key={r.id}
                  onClick={() => navigate(`/directory/pes/${r.id}`)}
                  className="grid grid-cols-[2fr_110px_110px_1fr_100px] gap-2 px-4 py-3 border-b border-white/5 items-center hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`admin-analytics-row-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-white text-sm truncate">{r.name || "—"}</div>
                    <div className="text-gray-500 text-xs font-mono truncate">{r.email}</div>
                  </div>
                  <div className="inline-flex items-center gap-1 text-[#FF8A85] font-mono text-sm tabular-nums"
                       data-testid={`admin-analytics-count-${r.id}`}>
                    <Heart size={12} weight="fill" /> {r.favorited_count}
                  </div>
                  <div className="inline-flex items-center gap-1 text-gray-400 font-mono text-sm tabular-nums">
                    <Users size={12} /> {r.unique_client_count}
                  </div>
                  <div className="text-xs font-mono text-gray-400 truncate">
                    <MapPin size={11} className="inline mr-1" />
                    {r.region ? `${r.region}, ${r.country || ""}` : (r.country || "—")}
                    <span className="mx-2 text-gray-600">·</span>
                    <Briefcase size={11} className="inline mr-1" />
                    {r.years_of_practice || 0}y
                  </div>
                  <div>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                      r.pe_status === "approved"
                        ? "border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/5"
                        : r.pe_status === "license_expired"
                          ? "border-[#FF8A85]/50 text-[#FF8A85] bg-[#FF8A85]/5"
                          : "border-white/10 text-gray-400"
                    }`}>
                      {r.pe_status || "draft"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs font-mono text-gray-500" data-testid="admin-analytics-total">
              {total} PE{total !== 1 ? "s" : ""} tracked
            </div>
          </>
        )}
      </div>
    </div>
  );
}
